import { lazy, Suspense } from "react";

const ProductDemo = lazy(() => import("./HeroDemo"));

const logos = [
  { src: "/logos/microsoft.webp", alt: "Microsoft", className: "h-8 md:h-10 w-auto", width: 300, height: 64 },
  { src: "/logos/zendesk.svg", alt: "Zendesk", className: "h-7 md:h-9 w-auto", width: 50, height: 36 },
];

// Headline split into word tokens. Animation runs purely via CSS so the
// <h1> renders immediately for LCP — words simply rise into place.
const HEADLINE_LINE_1 = ["When", "someone", "wants", "a", "demo,"];
const HEADLINE_LINE_2 = ["they", "get", "one."];

const Word = ({ children, delay, accent = false }: { children: string; delay: number; accent?: boolean }) => (
  <span
    className="inline-block opacity-0 hero-word"
    style={{ animationDelay: `${delay}ms` }}
  >
    {accent ? <em className="not-italic font-display italic text-near-black">{children}</em> : children}
    <span aria-hidden>&nbsp;</span>
  </span>
);

const Hero = () => {
  return (
    <section id="hero" className="relative bg-off-white overflow-hidden">
      {/* Aurora backdrop — slow drifting Vivid Pink + Signal Yellow blobs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="aurora-blob-a absolute rounded-full"
          style={{
            width: 720,
            height: 720,
            top: "-12%",
            left: "-8%",
            background: "radial-gradient(circle, rgba(255,107,157,0.18) 0%, rgba(255,107,157,0) 60%)",
            filter: "blur(80px)",
            willChange: "transform",
          }}
        />
        <div
          className="aurora-blob-b absolute rounded-full"
          style={{
            width: 820,
            height: 820,
            top: "-6%",
            right: "-12%",
            background: "radial-gradient(circle, rgba(255,211,105,0.22) 0%, rgba(255,211,105,0) 60%)",
            filter: "blur(90px)",
            willChange: "transform",
          }}
        />
        <div
          className="aurora-blob-a absolute rounded-full"
          style={{
            width: 600,
            height: 600,
            top: "30%",
            left: "30%",
            background: "radial-gradient(circle, rgba(255,107,157,0.10) 0%, rgba(255,107,157,0) 65%)",
            filter: "blur(100px)",
            animationDuration: "60s",
            willChange: "transform",
          }}
        />
        {/* Bottom-center yellow blob — lights the demo from below */}
        <div
          className="aurora-blob-c absolute rounded-full"
          style={{
            width: 900,
            height: 600,
            bottom: "-10%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "radial-gradient(ellipse, rgba(255,211,105,0.18) 0%, rgba(255,211,105,0) 65%)",
            filter: "blur(100px)",
            willChange: "transform",
          }}
        />
        {/* Subtle grain — single biggest "premium" trick */}
        <div className="grain-overlay" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-20 w-full">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          {/* Live-status eyebrow — pulsing pink dot, original copy */}
          <p
            className="inline-flex items-center gap-2 font-body text-sm font-medium uppercase tracking-[0.2em] text-near-black/60 mb-6 animate-hero-fade-in"
            style={{ animationDelay: '0s' }}
          >
            <span className="live-dot w-1.5 h-1.5 rounded-full bg-vivid-pink" />
            NEVER MAKE CUSTOMERS WAIT
          </p>

          {/* Headline — renders immediately, words rise via CSS */}
          <h1 className="font-display font-medium text-near-black text-4xl sm:text-5xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
            <span className="block">
              {HEADLINE_LINE_1.map((w, i) => (
                <Word key={i} delay={120 + i * 55}>{w}</Word>
              ))}
            </span>
            <span className="block">
              {HEADLINE_LINE_2.map((w, i) => (
                <Word
                  key={i}
                  delay={120 + (HEADLINE_LINE_1.length + i) * 55}
                  accent={w === "they" || w === "one."}
                >
                  {w}
                </Word>
              ))}
            </span>
          </h1>

          <p
            className="mt-8 text-near-black/70 font-body text-lg md:text-xl max-w-3xl leading-relaxed animate-hero-fade-in"
            style={{ animationDelay: "0.55s" }}
          >
            alvo runs live product demos for every customer, the moment they want one.<br />
            24/7. In 70+ languages. For sales, onboarding, and support.
          </p>

          {/* CTA cluster — primary + secondary + trust microcopy */}
          <div
            className="mt-10 animate-hero-fade-in"
            style={{ animationDelay: "0.8s" }}
          >
            <button
              data-tally-open="BzB7q5"
              data-tally-layout="modal"
              data-tally-width="400"
              data-tally-emoji-text="👋"
              data-tally-emoji-animation="wave"
              className="group relative font-body font-semibold bg-signal-yellow text-near-black px-8 py-4 text-base rounded-sm hover:brightness-110 transition-all"
            >
              Get early access
              <span className="absolute -top-4 -right-4 text-2xl opacity-0 group-hover:opacity-100 group-hover:animate-pop-in">🎯</span>
            </button>
          </div>
        </div>

        {/* Demo below CTA */}
        <div
          className="mt-16 w-full max-w-5xl mx-auto animate-hero-fade-in"
          style={{ animationDelay: "1.05s" }}
        >
          <Suspense fallback={<div style={{ minHeight: 400 }} />}>
            <ProductDemo />
          </Suspense>
        </div>

        {/* Alumni logo bar below demo */}
        <div
          className="mt-12 flex flex-col items-center animate-hero-fade-in"
          style={{ animationDelay: "1.3s" }}
        >
          <p className="font-body text-sm text-near-black/60 mb-4">Built by engineers and sales leaders from</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-10">
            {logos.map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
                loading="lazy"
                decoding="async"
                className={`${logo.className} object-contain`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom vignette — focuses the eye on the demo, not the logo bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[40%] pointer-events-none" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.04) 0%, rgba(10,10,10,0) 100%)" }} />
    </section>
  );
};

export default Hero;
